import { clearTrackingCookies, readConsent } from './trackingConsent';

interface PixelFunction {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: PixelFunction;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: PixelFunction;
    _fbq?: PixelFunction;
  }
}

export const getMetaPixelId = () => {
  const value = process.env.REACT_APP_META_PIXEL_ID || '';
  return /^\d{8,20}$/.test(value) ? value : '';
};

// Keep the pixel off local certificate/PDF tools and arbitrary URLs. The success
// route is allowed only after its checkout query string has been removed.
export const isMetaPage = () => {
  if (!['/', '/downloads', '/sobre-certy', '/success'].includes(window.location.pathname)) return false;
  const params = new URLSearchParams(window.location.search);
  return Array.from(params.entries()).every(([key, value]) =>
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid'].includes(key)
    && /^[a-zA-Z0-9_-]{1,250}$/.test(value));
};

export const syncMetaConsent = () => {
  const id = getMetaPixelId();
  const granted = readConsent()?.marketing === true;
  if (!granted || !id || !isMetaPage()) {
    window.fbq?.('consent', 'revoke');
    if (!granted) clearTrackingCookies(['_fbp', '_fbc']);
    return false;
  }

  if (!window.fbq) {
    const pixel = function (...args: unknown[]) {
      if (pixel.callMethod) pixel.callMethod(...args);
      else pixel.queue.push(args);
    } as PixelFunction;
    pixel.queue = [];
    pixel.push = pixel;
    pixel.loaded = true;
    pixel.version = '2.0';
    window.fbq = pixel;
    window._fbq = pixel;
  }

  if (!document.querySelector('script[data-certy-meta]')) {
    window.fbq('consent', 'grant');
    window.fbq('set', 'autoConfig', false, id);
    // No advanced matching or form/identity data is supplied to Meta.
    window.fbq('init', id);
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.dataset.certyMeta = id;
    script.referrerPolicy = 'origin';
    document.head.appendChild(script);
  } else {
    window.fbq('consent', 'grant');
  }
  return true;
};

type MetaEventName = 'PageView' | 'Lead' | 'Subscribe' | 'DownloadClick' | 'PricingClick';

export const trackMetaEvent = (
  name: MetaEventName,
  params: Record<string, string | number> = {},
  eventId?: string
) => {
  if (!syncMetaConsent()) return false;
  const command = ['PageView', 'Lead', 'Subscribe'].includes(name) ? 'trackSingle' : 'trackSingleCustom';
  if (eventId) {
    window.fbq?.(command, getMetaPixelId(), name, params, { eventID: eventId });
    return true;
  }
  window.fbq?.(command, getMetaPixelId(), name, params);
  return true;
};
